import '@assistant-ui/styles/index.css';
import '@assistant-ui/styles/markdown.css';
import './style.scss';
import * as React from 'react';
import { Alert, Dropdown, Offcanvas } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { SparkleIcon } from 'lucide-react';
import { DataPoint } from 'Interfaces/DataPoint';
import { userRoles } from 'Interfaces/User';
import { useAppSelector } from 'index';
import { getRequestConfig } from 'api';
import { getLocalization } from '../../global/global';

import { getUserAiAuth } from '../../actions/aiChatActions';
import Chat from './chat';
import { generatePdf } from './util';

interface Props {
  form: string;
  data: DataPoint[];
  disabled?: boolean;
}

export default function AiAnalysis(props: Props) {

  const clientPersist = useAppSelector(state => state.clientPersist);
  const forms = useAppSelector(state => state.forms.collection);
  const [show, setShow] = React.useState(false);
  const [token, setToken] = React.useState<string | null>(null);
  const [loadAttachments, setLoadAttachments] = React.useState(false);
  const [fileData, setFileData] = React.useState<string | null>(null);
  const { data, form } = props;

  React.useEffect(() => {
    const getToken = async () => {
      if (clientPersist.userName && clientPersist.key) {
        const json = await getUserAiAuth(clientPersist.userName, clientPersist.key);
        if (json && json.token) {
          setToken(json.token);
        } else {
          if (json.errorCode === '2002') {
            setToken('2002');
          } else {
            setToken('invalid');
          }
          toast(`${json.errorMessage}`, {
            autoClose : false,
            hideProgressBar: true,
            type: toast.TYPE.ERROR,
          });
        }
      }
    };
    if (show && token === null && `${clientPersist.package}`.toLowerCase() === 'custom') {
      void getToken();
    } else if (show && token) {
      if (loadAttachments) {
        void downloadData();
      } else {
        const file = generatePdf(data, forms);
        setFileData(`${file}`);
      }
    }
    if (!show) {
      setFileData(null);
    }
  }, [show]);

  React.useEffect(() => {
    if (token && token !== 'invalid') {
      if (loadAttachments) {
        void downloadData();
      } else {
        const file = generatePdf(data, forms);
        setFileData(`${file}`);
      }
    }
  }, [token]);

  const downloadData = async () => {
    const ids = data.map(d => d.id);
    const url = `/json/app/export/ai-analysis/${form}`;
    const headers = getRequestConfig();
    const response = await fetch(url, {
      ...headers, body: JSON.stringify(ids), method: 'POST'
    });

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const blob = await response.blob();

    const base = await new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        // reader.result = "data:application/pdf;base64,JVBERi0x..."
        const base64 = reader.result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    setFileData(base);
  };

  const body = token === null ? (
    <div>{getLocalization('loading')}</div>
  ) : token === 'invalid' || token === '2002' ? (
    <div>
      <Alert variant="danger">{getLocalization(token === '2002' ? 'aiBudgetExceeded' : 'errorAuthorizingAi')}</Alert>
    </div>
  ) : fileData ?  (
    <Chat
      token={token}
      data={fileData}
      dataPointId={data.length === 1 ? data[0].id : undefined}
    />
  ) : <div>{getLocalization('loading')}</div>;

  const onClick = (loadAttachments: boolean) => {
    if (!data.length) {
      toast(getLocalization('noAIDataPointsSelected'), {
        autoClose: false,
        closeButton: true,
        type: toast.TYPE.WARNING
      });
      return;
    }
    setLoadAttachments(loadAttachments);
    setShow(!show);
  };

  const canvas = (
    <Offcanvas
      show={show}
      onHide={() => setShow(false)}
      placement={'end'}
      className="w-75 ai-analysis-off-canvas"
      backdrop="static"
    >
      <Offcanvas.Header closeButton className="ai-chat-off-canvas-header">
        <Offcanvas.Title>{getLocalization('askAI')}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {body}
      </Offcanvas.Body>
    </Offcanvas>
  );
  /* const modal = (
    <AppModal
      title={getLocalization('generateChecklist')}
      visible={showModal}
      body={body}
      confirmText={getLocalization('close')}
      modalProps={{ size: 'xl' }}
      onConfirm={() => setShowModal(!showModal)}
    />
  );*/

  return (
    <>
      {canvas}
      <Dropdown
      >
        <Dropdown.Toggle
          variant="primary"
          size="sm"
          id="ask-ai-dropdown"
          disabled={
            `${clientPersist.package}`.toLowerCase() !== 'custom' ||
            clientPersist.roles === userRoles.ENUMERATOR
          }
        >
          <SparkleIcon className="me-1" size={18}/>
          {getLocalization('askAI')}
        </Dropdown.Toggle>
        <Dropdown.Menu className="">
          <Dropdown.Item as="div"
            onClick={() => {
              onClick(false);
            }}
          >
            {getLocalization('analyzeDataWithoutAttachments')}
          </Dropdown.Item>
          <Dropdown.Item as="div"
            onClick={() => {
              onClick(true);
            }}
          >
            {getLocalization('analyzeDataWithAttachments')}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
}
